import { useState } from 'react';
import type { ReactNode } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { TabGroup, TabButton } from '../../components/ui/Tabs/index.js';
import {
  useKnowledgeWikiDocument,
  useUpdateKnowledgeWikiDocument,
} from '../../hooks/useKnowledgeWiki.js';
import { DocumentEditor, WikiMarkdown } from '../../components/features/knowledge-wiki/index.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton/index.js';
import type { KnowledgeWikiDetail, KnowledgeWikiStructured } from '../../types/knowledge-wiki.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business コーディング規約 wiki の詳細ページ。理由・悪い例・良い例を項目ごとに並べて読ませる
 * @graph-connects useKnowledgeWikiDocument [calls] ドキュメント詳細フック呼び出し
 * @graph-connects useUpdateKnowledgeWikiDocument [calls] 項目別編集の保存フック呼び出し
 */
export const Route = createFileRoute('/knowledge-wiki/$id')({
  component: KnowledgeWikiDetailPage,
});

/** 項目 1 つ分の枠。未記入なら「未記入」を明示する。 @graph-connects none */
function Section({
  title,
  tone,
  children,
}: {
  title: string;
  tone?: 'bad' | 'good';
  children: ReactNode;
}) {
  const border =
    tone === 'bad'
      ? 'border-l-[var(--color-error)]'
      : tone === 'good'
        ? 'border-l-[var(--color-success)]'
        : 'border-l-[var(--color-border)]';
  return (
    <section className={`border-l-2 pl-4 ${border}`}>
      <h2 className="mb-1 text-sm font-semibold text-[var(--color-text-primary)]">{title}</h2>
      {children}
    </section>
  );
}

/** 未記入プレースホルダ。 @graph-connects none */
function Missing() {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-[var(--color-text-muted)] italic">{t('knowledgeWiki.notSet')}</p>
  );
}

/**
 * 構造化本文を項目ごとに描画する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business カテゴリごとの構造化本文（理由 / 悪い例 / 良い例 / 概要 / 補足）を項目単位で描画する
 * @graph-connects none
 */
function StructuredBody({ structured }: { structured: KnowledgeWikiStructured }) {
  const { t } = useTranslation();

  if (structured.kind === 'anti-pattern') {
    return (
      <div className="space-y-6">
        <Section title={t('knowledgeWiki.field.reason')}>
          {structured.reason ? <WikiMarkdown markdown={structured.reason} /> : <Missing />}
        </Section>
        <Section title={t('knowledgeWiki.field.badExample')} tone="bad">
          {structured.badExample ? (
            <WikiMarkdown markdown={structured.badExample.markdown} />
          ) : (
            <Missing />
          )}
        </Section>
        <Section title={t('knowledgeWiki.field.goodExample')} tone="good">
          {structured.goodExample ? (
            <WikiMarkdown markdown={structured.goodExample.markdown} />
          ) : (
            <Missing />
          )}
        </Section>
        {structured.notes && (
          <Section title={t('knowledgeWiki.field.notes')}>
            <WikiMarkdown markdown={structured.notes} />
          </Section>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Section title={t('knowledgeWiki.field.overview')}>
        {structured.overview ? <WikiMarkdown markdown={structured.overview} /> : <Missing />}
      </Section>
      {structured.codeExample && (
        <Section title={t('knowledgeWiki.field.codeExample')}>
          <WikiMarkdown markdown={structured.codeExample.markdown} />
        </Section>
      )}
      {structured.notes && (
        <Section title={t('knowledgeWiki.field.notes')}>
          <WikiMarkdown markdown={structured.notes} />
        </Section>
      )}
    </div>
  );
}

/**
 * 言語切替。記事は locale ごとに本文を持つので、UI 言語に関係なく
 * どの言語版を読む / 直すかを選べるようにする。
 * @graph-connects none
 */
function LocaleSwitch({
  detail,
  locale,
  onChange,
}: {
  detail: KnowledgeWikiDetail;
  locale: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  if (detail.availableLocales.length < 2) {
    return (
      <span className="text-xs text-[var(--color-text-muted)]">
        {t('knowledgeWiki.onlyLocale', { locale: detail.localeCode })}
      </span>
    );
  }
  return (
    <TabGroup ariaLabel={t('knowledgeWiki.localeSwitchLabel')}>
      {detail.availableLocales.map((code) => (
        <TabButton
          key={code}
          active={locale === code}
          label={code}
          onClick={() => {
            onChange(code);
          }}
        />
      ))}
    </TabGroup>
  );
}

/** 見出し部（タイトル・カテゴリ・タグ・未記入バッジ）。 @graph-connects none */
function DetailHeader({
  detail,
  locale,
  onLocaleChange,
  onEdit,
}: {
  detail: KnowledgeWikiDetail;
  locale: string;
  onLocaleChange: (next: string) => void;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <Link
        to="/knowledge-wiki"
        className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        ← {t('knowledgeWiki.backToList')}
      </Link>
      <div className="mt-2 flex items-start justify-between gap-4">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{detail.title}</h1>
        <div className="flex shrink-0 items-center gap-2">
          <LocaleSwitch detail={detail} locale={locale} onChange={onLocaleChange} />
          {onEdit && (
            <Button variant="secondary" size="sm" onClick={onEdit}>
              {t('knowledgeWiki.edit.editButton')}
            </Button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Badge variant="info">
          {t(`knowledgeWiki.category.${detail.category}`, { defaultValue: detail.category })}
        </Badge>
        {detail.missingFields.length > 0 && (
          <Badge variant="warning">
            {t('knowledgeWiki.incompleteBadge', { count: detail.missingFields.length })}
          </Badge>
        )}
        {detail.tags.map((tag) => (
          <span
            key={tag.id}
            className="rounded px-1.5 py-0.5 text-xs text-[var(--color-text-muted)] bg-[var(--color-bg-base)]"
          >
            {tag.label}
          </span>
        ))}
      </div>
      {detail.description && (
        <p className="mt-2 text-[var(--color-text-secondary)]">{detail.description}</p>
      )}
    </div>
  );
}

/**
 * コーディング規約 wiki 詳細ページ
 * @graph-connects none
 */
function KnowledgeWikiDetailPage() {
  const { id } = Route.useParams();
  const { t, i18n } = useTranslation();
  // 初期表示は UI 言語に合わせ、以降は画面上の切替を優先する
  const [locale, setLocale] = useState(i18n.language.startsWith('en') ? 'en' : 'ja');
  const { data, isLoading, isError, refetch } = useKnowledgeWikiDocument(id, locale);
  const update = useUpdateKnowledgeWikiDocument(id);
  const [isEditing, setIsEditing] = useState(false);

  const changeLocale = (next: string): void => {
    // 編集中に言語を変えると入力中の内容がどの言語のものか分からなくなる
    update.reset();
    setIsEditing(false);
    setLocale(next);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-6 animate-fade-in">
        <ErrorState
          message={t('knowledgeWiki.loadError')}
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  // 構造化スキーマを持たないカテゴリは項目に分けられないため編集させない
  // （旧画面の Markdown 編集が引き続き受け持つ）。
  const canEdit = data.structured !== null;

  if (isEditing && data.structured) {
    return (
      <div className="space-y-6 animate-fade-in">
        <DetailHeader detail={data} locale={locale} onLocaleChange={changeLocale} />
        <Card>
          <CardContent>
            <DocumentEditor
              detail={data}
              isSaving={update.isPending}
              errorMessage={update.isError ? t('knowledgeWiki.edit.saveError') : null}
              onCancel={() => {
                update.reset();
                setIsEditing(false);
              }}
              onSave={(input) => {
                update.mutate(input, {
                  onSuccess: () => {
                    setIsEditing(false);
                  },
                });
              }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <DetailHeader
        detail={data}
        locale={locale}
        onLocaleChange={changeLocale}
        onEdit={
          canEdit
            ? () => {
                setIsEditing(true);
              }
            : undefined
        }
      />
      <Card>
        <CardContent>
          {data.structured ? (
            <StructuredBody structured={data.structured} />
          ) : (
            <WikiMarkdown markdown={data.body} />
          )}
        </CardContent>
      </Card>
      <p className="text-xs text-[var(--color-text-muted)]">
        {t('knowledgeWiki.updatedAt', { date: data.updatedAt?.slice(0, 10) ?? '—' })}
      </p>
    </div>
  );
}
